import { useCallback, useMemo } from 'react';
import { css } from '@emotion/css';
import { noop } from 'lodash';

import useScreen from '@lyearn/core/hooks/useScreen';
import { useTranslation } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
import { Modal } from '@/components/Modals';
import useNavigation from '@/hooks/useNavigation';

import { RetryModalProps } from './types';
import { retryModalHeaderRenderer } from './util';
export const modalStyles = css`
  width: 38.2rem;
  max-height: 38.2rem;
  min-height: 25.6rem;
  padding-bottom: 2.4rem;
  min-width: 25rem;
  &.full-height {
    min-height: 68rem;
  }

  @media (max-height: 700px) {
    max-height: 90%;
    &.full-height {
      min-height: 90%;
    }
  }
`;

export const landscapeModalStyles = css`
  width: 38.2rem;
  max-height: 38.2rem;
  min-height: 25.6rem;
  padding-bottom: 2.4rem;
  min-width: 25rem;
  &.full-height {
    min-height: 68rem;
  }

  @media (max-height: 700px) {
    max-height: 90%;
    &.full-height {
      min-height: 90%;
    }
  }

  transform: rotate(270deg) !important;
`;
export const actionStyles = css`
  padding-bottom: 1.2rem;
`;

function RetryModal(props: RetryModalProps) {
  const {
    noOfAttenptsLeft = 0,
    setIsFaceVerificationModalOpen,
    setIsRetryDialogOpen,
    message,
  } = props;
  const { isLandScapeMode } = useScreen();
  const { t } = useTranslation(['ExternalCamera']);
  const displayMessage = useMemo(() => {
    if (noOfAttenptsLeft > 0) return message ?? t('ExternalCamera:FaceVerification.DoesNotMatch');
    return t('ExternalCamera:FaceVerification.NoAttemptsLeft');
  }, [noOfAttenptsLeft, message]);
  const navigation = useNavigation();
  const subFooterRenderer = useCallback(() => {
    return (
      <Stack className="w-full" justifyContent="center">
        <Typography color="text-text-secondary" variant="body-short-03">
          {t('ExternalCamera:FaceVerification.AttemptsLeft', { noOfAttenptsLeft })}
        </Typography>
      </Stack>
    );
  }, [noOfAttenptsLeft]);
  const onRetry = useCallback(() => {
    if (noOfAttenptsLeft <= 0) {
      navigation.push('/', {
        appendQuery: false,
      });
    } else {
      setIsFaceVerificationModalOpen(true);
      setIsRetryDialogOpen(false);
    }
  }, [setIsFaceVerificationModalOpen, setIsRetryDialogOpen]);
  return (
    <Modal
      action={{
        text: noOfAttenptsLeft <= 0 ? t('Common:Actions.BackToHome') : t('Common:Actions.TryAgain'),
        onSubmit: onRetry,
        disabled: false,
        className: actionStyles,
      }}
      classes={{ paper: isLandScapeMode ? landscapeModalStyles : modalStyles }}
      header={{
        title: t('ExternalCamera:FaceVerification.VerificationFailedHeading'),
        description: displayMessage,
      }}
      renderAs="dialog"
      renderHeader={retryModalHeaderRenderer}
      renderSubFooter={noOfAttenptsLeft > 0 ? subFooterRenderer : undefined}
      showCloseBtn={false}
      showCloseIcon={false}
      onClose={noop}></Modal>
  );
}

export default RetryModal;
